import type { Checklist, ChecklistsPage } from '@cobliteam/api-lib';

/**
 * Ryuk answers `completedChecklists` through a plain Spring `Pageable`, so a single request only ever
 * carries one page — there is no "give me everything" size. Walking the pages is what makes a full
 * export possible, and the walk is bounded so one click on a large fleet can't turn into an unbounded
 * fetch loop.
 */
export const COMPLETED_CHECKLISTS_EXPORT_PAGE_SIZE = 500;
export const COMPLETED_CHECKLISTS_EXPORT_MAX_PAGES = 20;
export const COMPLETED_CHECKLISTS_EXPORT_MAX_ROWS = COMPLETED_CHECKLISTS_EXPORT_PAGE_SIZE * COMPLETED_CHECKLISTS_EXPORT_MAX_PAGES;

export interface AllCompletedChecklists {
  checklists: Checklist[];
  /** True when the walk stopped at the page ceiling while the backend still had responses left. */
  truncated: boolean;
}

/**
 * Collects every completed checklist a paginated query can reach, page by page.
 * `fetchPage` is injected so the caller decides how a page is fetched (react-query, plain requester).
 */
export const collectAllCompletedChecklists = async (
  fetchPage: (page: number) => Promise<ChecklistsPage>,
  maxPages: number = COMPLETED_CHECKLISTS_EXPORT_MAX_PAGES
): Promise<AllCompletedChecklists> => {
  const checklists: Checklist[] = [];

  for (let page = 0; page < maxPages; page++) {
    const { page: pageResult } = await fetchPage(page);
    const content = pageResult?.content ?? [];

    checklists.push(...content);

    if (pageResult?.last || !content.length) return { checklists, truncated: false };
  }

  return { checklists, truncated: true };
};
